import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import api3003 from "../../services/api3003";

/* -------------------- RELEVÂNCIA -------------------- */

const calcularRelevancia = (cliente, termo) => {
  if (!termo) return 0;

  termo = termo.toLowerCase();

  let relevancia = 0;

  if (cliente.nome?.toLowerCase().startsWith(termo)) relevancia += 3;
  if (cliente.nome?.toLowerCase().includes(termo)) relevancia += 2;
  if (cliente.email?.toLowerCase().includes(termo)) relevancia += 1;

  return relevancia;
};

/* -------------------- ESTILOS -------------------- */

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.li`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const LoadingText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

/* -------------------- COMPONENTE -------------------- */

const ClientSearch = ({ token, value, onSelect }) => {
  const [termo, setTermo] = useState(value || "");
  const [debouncedTermo, setDebouncedTermo] = useState(termo);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(value || null);

  const cache = useRef({});
  const cancelToken = useRef(null);

  /* -------------------- SINCRONIZAR VALUE EXTERNO -------------------- */

  useEffect(() => {
    if (value) {
      setSelectedClient(value);
      setTermo(value);
    }
  }, [value]);

  /* -------------------- DEBOUNCE -------------------- */

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTermo(termo);
    }, 400);

    return () => clearTimeout(timer);
  }, [termo]);

  /* -------------------- BUSCA -------------------- */

  useEffect(() => {
    const buscar = async () => {
      if (!debouncedTermo || debouncedTermo.length < 2 || selectedClient) {
        setClientes([]);
        return;
      }

      const termoBusca = debouncedTermo.toLowerCase();

      /* CACHE */

      if (cache.current[termoBusca]) {
        setClientes(cache.current[termoBusca]);
        return;
      }

      try {
        setLoading(true);

        /* cancelar request anterior */

        if (cancelToken.current) {
          cancelToken.current.abort();
        }

        cancelToken.current = new AbortController();

        const res = await api3003.get(
          `/api/clients?search=${termoBusca}&limit=10`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: cancelToken.current.signal,
          }
        );

        const ordenados = res.data
          .map((c) => ({
            ...c,
            relevancia: calcularRelevancia(c, termoBusca),
          }))
          .sort((a, b) => b.relevancia - a.relevancia)
          .slice(0, 10);

        cache.current[termoBusca] = ordenados;

        setClientes(ordenados);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Erro ao buscar clientes:", err);
        }

        setClientes([]);
      } finally {
        setLoading(false);
      }
    };

    if (showSuggestions) {
      buscar();
    }
  }, [debouncedTermo, token, showSuggestions, selectedClient]);

  /* -------------------- INPUT -------------------- */

  const handleInputChange = (e) => {
    setTermo(e.target.value);
    setSelectedClient(null);
  };

  /* -------------------- SELECT -------------------- */

  const handleSelect = (cliente) => {
    const clienteSelecionado = { nome: cliente.nome };

    setSelectedClient(clienteSelecionado);
    setTermo(cliente.nome);
    setClientes([]);
    setShowSuggestions(false);

    if (onSelect) onSelect(clienteSelecionado);
  };

  /* -------------------- BLUR -------------------- */

  const handleBlur = () => {
    if (!selectedClient) {
      setTermo("");
    }

    setTimeout(() => setShowSuggestions(false), 150);
  };

  /* -------------------- RENDER -------------------- */

  return (
    <Container>
      <Input
        type="text"
        placeholder="Buscar cliente..."
        value={termo}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={handleBlur}
      />

      {loading && <LoadingText>Carregando...</LoadingText>}

      {showSuggestions && clientes.length > 0 && (
        <SuggestionsList>
          {clientes.map((c) => (
            <SuggestionItem
              key={c.id}
              onMouseDown={() => handleSelect(c)}
            >
              {c.nome}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </Container>
  );
};

export default ClientSearch;